import {Controller} from "@hotwired/stimulus"

/**
 * SortingController allows to sort table rows based on a specified numeric
 * column. It expects two buttons in the header cell:
 *
 *    %button{
 *      type: :button,
 *      data: {
 *        action: "sorting#sort",
 *        sortColumn: "col_a",
 *        sortDirection: "asc"
 *      }
 *    } ▲
 *
 * And then for the cell to be marked
 *
 *    %td{data: { sortColumn: "col_a", sortValue: "123.45" }}
 *
 * The header button & data cells must have the same `sortColumn` value.
 * `sortValue` should be a numeric value that can be parsed as a float, missing
 * & unparseable values will be treated as `null` and sorted accordingly.
 *
 * Current sorting is indicated by a CSS class, which is added to the button.
 *
 * If a row is added after the initial sort, it will be sorted automatically
 * based on the current ordering if it's marked as a `row` target.
 *
 * Rows can be marked as dependentRow with a `data-sorting-depends-on` attribute,
 * in which case they will be sorted immediately after the row they depend on.
 *
 * Rows can be marked as trailingRow, in this case, it will always be sorted to
 * the bottom of the table.
 */
export default class extends Controller {
  static targets = ['row', 'dependentRow', 'trailingRow']
  static classes = ["current"]

  sort(event) {
    this.element.querySelectorAll('[data-action~="sorting#sort"]').forEach(el => {
      el.classList.remove(this.currentClass);
    })
    event.currentTarget.classList.add(this.currentClass);

    this._sort(event.currentTarget);
  }

  rowTargetConnected(row) {
    this._sort(this._activeSortingButton());
  }

  _activeSortingButton() {
    return this.element.querySelector(`.${this.currentClass}[data-action~="sorting#sort"]`);
  }

  _sort(button) {
    const column = button.dataset.sortColumn
    const direction = button.dataset.sortDirection
    const tbody = this.element.querySelector("tbody")
    const rows = Array.from(this.rowTargets)

    rows.sort((a, b) => {
      const aCell = a.querySelector(`[data-sort-col='${column}']`)
      const bCell = b.querySelector(`[data-sort-col='${column}']`)
      let aValue = aCell ? aCell.dataset.sortValue : null
      let bValue = bCell ? bCell.dataset.sortValue : null

      // Convert to number if possible, otherwise set to null
      aValue = aValue !== undefined && aValue !== null && aValue !== "" && !isNaN(aValue) ? parseFloat(aValue) : null
      bValue = bValue !== undefined && bValue !== null && bValue !== "" && !isNaN(bValue) ? parseFloat(bValue) : null

      // Sort nulls to the end for asc, to the start for desc
      if (aValue === null && bValue === null) return 0
      if (aValue === null) return direction === "asc" ? 1 : -1
      if (bValue === null) return direction === "asc" ? -1 : 1

      if (direction === "asc") {
        return aValue - bValue
      } else {
        return bValue - aValue
      }
    })

    // Re-append sorted rows
    rows.forEach(row => {
      tbody.appendChild(row)

      this.dependentRowTargets.forEach(dependentRow => {
        if (dependentRow.dataset.sortingDependsOn === row.id) {
          tbody.appendChild(dependentRow);
        }
      })
    })

    // keep trailing rows at the bottom
    this.trailingRowTargets.forEach(trailingRow => {
      tbody.appendChild(trailingRow);
    })
  }
}
